import {
  createMp4StreamRecorder,
  isMp4MediabunnyRecordingSupported,
} from './stream-mp4-mediabunny-recorder.js';
import { uploadRecording } from './stream-recording-upload.js';

function showToast(message, type = 'info') {
  if (typeof window.showSiteToast === 'function') {
    window.showSiteToast(message, type);
    return;
  }

  alert(message);
}

const RECORDER_BITRATE = {
  videoBitsPerSecond: 750_000,
  audioBitsPerSecond: 48_000,
};

function pickRecorderMimeType() {
  if (typeof MediaRecorder === 'undefined') {
    return '';
  }

  const candidates = [
    'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
    'video/mp4;codecs=h264,aac',
    'video/mp4',
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
  ];

  return candidates.find((type) => MediaRecorder.isTypeSupported(type)) || '';
}

function pickRecordingBackend() {
  const nativeMime = pickRecorderMimeType();

  if (nativeMime.includes('mp4')) {
    return { mode: 'native', mimeType: nativeMime };
  }

  if (isMp4MediabunnyRecordingSupported()) {
    return { mode: 'mediabunny', mimeType: 'video/mp4' };
  }

  if (nativeMime) {
    return { mode: 'native', mimeType: nativeMime };
  }

  return null;
}

function captureVideoStream(video) {
  if (!video) {
    return null;
  }

  if (typeof video.captureStream === 'function') {
    return video.captureStream();
  }

  if (typeof video.mozCaptureStream === 'function') {
    return video.mozCaptureStream();
  }

  return null;
}

function resolveRecordingSession(liveController, wrap) {
  if (typeof liveController?.resolveRecordingSession === 'function') {
    return liveController.resolveRecordingSession();
  }

  const playerWrap = wrap || liveController.getPlayerWrap?.();
  if (!playerWrap?.classList.contains('is-open')) {
    return null;
  }

  const channel = liveController.getActiveKey?.();
  const video = liveController.getActiveVideo?.() || playerWrap.querySelector('video');

  if (!channel || !video || video.paused || video.readyState < 2) {
    return null;
  }

  return { key: channel, video };
}

function formatRecordingDuration(seconds) {
  const total = Math.max(1, Math.round(Number(seconds) || 0));
  const minutes = Math.floor(total / 60);
  const secs = total % 60;

  return `${minutes}:${String(secs).padStart(2, '0')}`;
}

function formatRecordingSize(bytes) {
  const size = Math.max(0, Number(bytes) || 0);
  if (size >= 1048576) {
    return `${(size / 1048576).toFixed(1)} MB`;
  }

  if (size >= 1024) {
    return `${Math.round(size / 1024)} KB`;
  }

  return `${size} B`;
}

function isRecordingActive(mediaRecorder, mp4Recorder) {
  return Boolean(mp4Recorder)
    || (mediaRecorder && mediaRecorder.state === 'recording');
}

export function initStreamLiveRecorder({
  liveController,
  wrapId = 'gv-top-player-wrap',
  page,
} = {}) {
  if (!liveController || page?.dataset.streamRecorder !== '1') {
    return () => {};
  }

  const uploadUrl = page.dataset.streamRecordingUpload?.trim();
  const adminUrl = page.dataset.streamRecordingsAdmin?.trim();
  const galleryUploadUrl = page.dataset.streamGalleryUpload?.trim();

  if (!uploadUrl) {
    return () => {};
  }

  const wrap = document.getElementById(wrapId);
  const controls = wrap?.querySelector('.product-live-streams__controls');

  if (!controls || controls.querySelector('.product-live-streams__record-btn')) {
    return () => {};
  }

  const recordBtn = document.createElement('button');
  recordBtn.type = 'button';
  recordBtn.className = 'product-live-streams__ctrl-btn product-live-streams__record-btn';
  recordBtn.setAttribute('aria-label', 'ضبط استریم');
  recordBtn.title = 'ضبط استریم';
  recordBtn.textContent = '⏺';
  recordBtn.hidden = true;

  const fsBtn = controls.querySelector('.product-live-streams__fs-toggle');
  if (fsBtn) {
    controls.insertBefore(recordBtn, fsBtn);
  } else {
    controls.appendChild(recordBtn);
  }

  let mediaRecorder = null;
  let mp4Recorder = null;
  let recordedChunks = [];
  let startedAt = null;
  let activeChannel = null;
  let uploading = false;
  let pendingRecording = null;
  let pollTimer = null;

  const savePanel = document.createElement('div');
  savePanel.className = 'stream-recording-save-panel';
  savePanel.hidden = true;
  savePanel.innerHTML =
    '<div class="stream-recording-save-panel__dialog" role="dialog" aria-modal="true" aria-labelledby="stream-recording-save-title">'
    + '<p class="stream-recording-save-panel__title" id="stream-recording-save-title">ضبط تمام شد</p>'
    + '<p class="stream-recording-save-panel__meta" data-recording-meta></p>'
    + '<p class="stream-recording-save-panel__question">این ضبط ذخیره شود؟</p>'
    + '<div class="stream-recording-save-panel__progress-wrap" hidden>'
    + '<div class="stream-recording-save-panel__progress-track">'
    + '<div class="stream-recording-save-panel__progress-bar" style="width:0%"></div>'
    + '</div>'
    + '<span class="stream-recording-save-panel__progress-label">در حال آپلود… ۰٪</span>'
    + '</div>'
    + '<div class="stream-recording-save-panel__actions">'
    + '<button type="button" class="stream-recording-save-panel__btn stream-recording-save-panel__btn--yes">بله، ذخیره</button>'
    + '<button type="button" class="stream-recording-save-panel__btn stream-recording-save-panel__btn--gallery" data-recording-gallery hidden>افزودن به گالری</button>'
    + '<button type="button" class="stream-recording-save-panel__btn stream-recording-save-panel__btn--no">خیر</button>'
    + '</div>'
    + '</div>';
  wrap?.appendChild(savePanel);

  const saveMetaEl = savePanel.querySelector('[data-recording-meta]');
  const saveProgressWrap = savePanel.querySelector('.stream-recording-save-panel__progress-wrap');
  const saveProgressBar = savePanel.querySelector('.stream-recording-save-panel__progress-bar');
  const saveProgressLabel = savePanel.querySelector('.stream-recording-save-panel__progress-label');
  const saveActionsEl = savePanel.querySelector('.stream-recording-save-panel__actions');
  const saveYesBtn = savePanel.querySelector('.stream-recording-save-panel__btn--yes');
  const saveNoBtn = savePanel.querySelector('.stream-recording-save-panel__btn--no');
  const saveGalleryBtn = savePanel.querySelector('[data-recording-gallery]');

  if (saveGalleryBtn && galleryUploadUrl) {
    saveGalleryBtn.hidden = false;
  }

  const setSavePanelOpen = (open) => {
    savePanel.hidden = !open;
    savePanel.setAttribute('aria-hidden', open ? 'false' : 'true');
  };

  const resetSavePanelUi = () => {
    saveProgressWrap.hidden = true;
    saveActionsEl.hidden = false;
    saveProgressBar.style.width = '0%';
    saveProgressLabel.textContent = 'در حال آپلود… ۰٪';
    saveYesBtn.disabled = false;
    saveNoBtn.disabled = false;
    if (saveGalleryBtn) {
      saveGalleryBtn.disabled = false;
    }
  };

  const hideSavePanel = () => {
    pendingRecording = null;
    resetSavePanelUi();
    setSavePanelOpen(false);
    syncRecordButton();
  };

  const showSavePrompt = (recording) => {
    pendingRecording = recording;
    resetSavePanelUi();

    const durationSeconds = Math.max(
      1,
      Math.round((Date.now() - recording.recordingStartedAt.getTime()) / 1000),
    );

    saveMetaEl.textContent = `مدت: ${formatRecordingDuration(durationSeconds)} · حجم: ${formatRecordingSize(recording.blob.size)}`;
    setSavePanelOpen(true);
    syncRecordButton();
  };

  const setUploadProgress = (percent) => {
    const safe = Math.max(0, Math.min(100, Number(percent) || 0));
    saveProgressBar.style.width = `${safe}%`;
    saveProgressLabel.textContent = safe >= 100
      ? 'در حال نهایی‌سازی…'
      : `در حال آپلود… ${safe}٪`;
  };

  const setRecordingUi = (recording) => {
    recordBtn.classList.toggle('is-recording', recording);
    recordBtn.title = recording ? 'توقف ضبط' : 'ضبط استریم';
    recordBtn.setAttribute('aria-label', recording ? 'توقف ضبط' : 'ضبط استریم');
  };

  const resetRecorder = () => {
    mediaRecorder = null;
    mp4Recorder = null;
    recordedChunks = [];
    startedAt = null;
    activeChannel = null;
    setRecordingUi(false);
  };

  const cancelActiveRecorder = async () => {
    if (mp4Recorder) {
      const recorder = mp4Recorder;
      mp4Recorder = null;
      await recorder.cancel();
    }

    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
    }

    resetRecorder();
  };

  const syncRecordButton = () => {
    const session = resolveRecordingSession(liveController, wrap);
    const isOpen = wrap?.classList.contains('is-open');
    const canRecord = Boolean(
      isOpen
      && session
      && !uploading
      && !pendingRecording
      && pickRecordingBackend(),
    );

    recordBtn.hidden = !canRecord && !isRecordingActive(mediaRecorder, mp4Recorder);

    if (isRecordingActive(mediaRecorder, mp4Recorder)) {
      recordBtn.hidden = false;
    }

    recordBtn.disabled = uploading;
  };

  const persistRecording = async ({
    blob,
    mimeType,
    channel,
    recordingStartedAt,
  }) => {
    if (!channel || blob.size === 0) {
      showToast('ضبط خالی بود.', 'warning');
      return;
    }

    uploading = true;
    saveActionsEl.hidden = true;
    saveProgressWrap.hidden = false;
    setUploadProgress(0);
    syncRecordButton();

    try {
      const durationSeconds = Math.max(1, Math.round((Date.now() - recordingStartedAt.getTime()) / 1000));
      const result = await uploadRecording({
        blob,
        mimeType,
        channel,
        page,
        startedAt: recordingStartedAt,
        durationSeconds,
        uploadUrl,
        onProgress: setUploadProgress,
      });

      hideSavePanel();

      const savedUrl = result?.data?.admin_url || adminUrl;
      showToast(
        savedUrl
          ? `ضبط MP4 ذخیره شد. <a href="${savedUrl}" target="_blank" rel="noopener">مشاهده در پنل</a>`
          : 'ضبط MP4 ذخیره شد.',
        'success',
      );
    } catch (error) {
      saveActionsEl.hidden = false;
      saveProgressWrap.hidden = true;
      showToast(error?.message || 'خطا در ذخیره ضبط', 'error');
    } finally {
      uploading = false;
      syncRecordButton();
    }
  };

  const addPendingToGallery = async () => {
    if (!pendingRecording || uploading || !galleryUploadUrl) {
      return;
    }

    const recording = pendingRecording;

    if (!recording.channel || recording.blob.size === 0) {
      showToast('ضبط خالی بود.', 'warning');
      return;
    }

    uploading = true;
    saveActionsEl.hidden = true;
    saveProgressWrap.hidden = false;
    setUploadProgress(0);
    syncRecordButton();

    try {
      const durationSeconds = Math.max(
        1,
        Math.round((Date.now() - recording.recordingStartedAt.getTime()) / 1000),
      );
      const result = await uploadRecording({
        blob: recording.blob,
        mimeType: recording.mimeType,
        channel: recording.channel,
        page,
        startedAt: recording.recordingStartedAt,
        durationSeconds,
        uploadUrl: galleryUploadUrl,
        onProgress: setUploadProgress,
      });

      hideSavePanel();
      showToast(result?.message || 'ضبط به گالری محصولات اضافه شد.', 'success');
    } catch (error) {
      saveActionsEl.hidden = false;
      saveProgressWrap.hidden = true;
      showToast(error?.message || 'افزودن به گالری ناموفق بود.', 'error');
    } finally {
      uploading = false;
      syncRecordButton();
    }
  };

  const discardPendingRecording = () => {
    hideSavePanel();
    showToast('ضبط ذخیره نشد.', 'info');
  };

  const confirmPendingRecording = async () => {
    if (!pendingRecording || uploading) {
      return;
    }

    const recording = pendingRecording;
    await persistRecording(recording);
  };

  const finishRecording = async () => {
    if (mp4Recorder) {
      const recorder = mp4Recorder;
      const channel = activeChannel;
      const recordingStartedAt = startedAt || new Date();

      mp4Recorder = null;
      setRecordingUi(false);

      try {
        const { blob, mimeType } = await recorder.stop();
        resetRecorder();

        if (blob.size === 0) {
          showToast('ضبط خالی بود.', 'warning');
          syncRecordButton();
          return;
        }

        showSavePrompt({
          blob,
          mimeType,
          channel,
          recordingStartedAt,
        });
      } catch (error) {
        await recorder.cancel().catch(() => {});
        resetRecorder();
        showToast(error?.message || 'خطا در پایان ضبط MP4', 'error');
        syncRecordButton();
      }

      return;
    }

    if (!mediaRecorder || mediaRecorder.state === 'inactive') {
      resetRecorder();
      syncRecordButton();
      return;
    }

    const recorder = mediaRecorder;
    const channel = activeChannel;
    const recordingStartedAt = startedAt || new Date();

    await new Promise((resolve) => {
      recorder.addEventListener('stop', resolve, { once: true });
      recorder.stop();
    });

    const blob = new Blob(recordedChunks, { type: recorder.mimeType || 'video/webm' });
    resetRecorder();

    if (blob.size === 0) {
      showToast('ضبط خالی بود.', 'warning');
      syncRecordButton();
      return;
    }

    showSavePrompt({
      blob,
      mimeType: blob.type,
      channel,
      recordingStartedAt,
    });
  };

  const startRecording = async () => {
    const session = resolveRecordingSession(liveController, wrap);
    const backend = pickRecordingBackend();

    if (!session) {
      const playerWrap = wrap || liveController.getPlayerWrap?.();
      const video = liveController.getActiveVideo?.() || playerWrap?.querySelector('video');
      if (playerWrap?.classList.contains('is-open') && video && video.paused) {
        showToast('ابتدا پخش استریم را از دکمه ▶ شروع کنید.', 'warning');
      } else {
        showToast('ابتدا یک استریم را پخش کنید.', 'warning');
      }
      return;
    }

    const channel = session.key;
    const video = session.video;

    if (!backend) {
      showToast('مرورگر شما از ضبط ویدیو پشتیبانی نمی‌کند.', 'error');
      return;
    }

    const stream = captureVideoStream(video);
    if (!stream) {
      showToast('امکان ضبط این پخش در مرورگر وجود ندارد.', 'error');
      return;
    }

    recordedChunks = [];
    startedAt = new Date();
    activeChannel = channel;

    if (backend.mode === 'mediabunny') {
      mp4Recorder = createMp4StreamRecorder(RECORDER_BITRATE);

      try {
        await mp4Recorder.start(stream);
      } catch (error) {
        await mp4Recorder.cancel().catch(() => {});
        mp4Recorder = null;
        resetRecorder();
        showToast(error?.message || 'شروع ضبط MP4 ممکن نشد.', 'error');
        return;
      }

      setRecordingUi(true);
      showToast('ضبط MP4 شروع شد.', 'success');
      syncRecordButton();
      return;
    }

    try {
      mediaRecorder = new MediaRecorder(stream, {
        mimeType: backend.mimeType,
        ...RECORDER_BITRATE,
      });
    } catch (error) {
      resetRecorder();
      showToast('شروع ضبط ممکن نشد.', 'error');
      return;
    }

    mediaRecorder.addEventListener('dataavailable', (event) => {
      if (event.data && event.data.size > 0) {
        recordedChunks.push(event.data);
      }
    });

    mediaRecorder.addEventListener('error', () => {
      showToast('خطا در حین ضبط.', 'error');
      resetRecorder();
      syncRecordButton();
    });

    mediaRecorder.start(1000);
    setRecordingUi(true);
    showToast(backend.mimeType.includes('mp4') ? 'ضبط MP4 شروع شد.' : 'ضبط شروع شد.', 'success');
    syncRecordButton();
  };

  const onRecordClick = async () => {
    if (uploading || pendingRecording) {
      return;
    }

    if (isRecordingActive(mediaRecorder, mp4Recorder)) {
      recordBtn.disabled = true;
      try {
        await finishRecording();
      } finally {
        recordBtn.disabled = false;
      }
      return;
    }

    await startRecording();
  };

  const onChannelChange = async () => {
    if (isRecordingActive(mediaRecorder, mp4Recorder)) {
      await finishRecording();
    }
  };

  let previousChannel = resolveRecordingSession(liveController, wrap)?.key || null;

  pollTimer = window.setInterval(async () => {
    const currentChannel = resolveRecordingSession(liveController, wrap)?.key || null;

    if (previousChannel !== currentChannel) {
      previousChannel = currentChannel;
      await onChannelChange();
    }

    syncRecordButton();
  }, 500);

  recordBtn.addEventListener('click', onRecordClick);
  saveYesBtn.addEventListener('click', confirmPendingRecording);
  saveNoBtn.addEventListener('click', discardPendingRecording);
  saveGalleryBtn?.addEventListener('click', addPendingToGallery);

  const video = wrap?.querySelector('video');
  video?.addEventListener('play', syncRecordButton);
  video?.addEventListener('pause', syncRecordButton);

  syncRecordButton();

  return () => {
    window.clearInterval(pollTimer);
    recordBtn.removeEventListener('click', onRecordClick);
    saveYesBtn.removeEventListener('click', confirmPendingRecording);
    saveNoBtn.removeEventListener('click', discardPendingRecording);
    saveGalleryBtn?.removeEventListener('click', addPendingToGallery);
    video?.removeEventListener('play', syncRecordButton);
    video?.removeEventListener('pause', syncRecordButton);

    if (isRecordingActive(mediaRecorder, mp4Recorder)) {
      cancelActiveRecorder();
    }

    pendingRecording = null;
    savePanel.remove();
    recordBtn.remove();
    resetRecorder();
  };
}
